import { useEffect, useRef, useState } from 'react';
import TimelineEvent from './TimelineEvent';
import type { FilterState, TimelineProps } from './types';

const Timeline = <
  T extends { id: string; title: string; category: string; date: Date | string }
>({
  events,
  className = '',
  isLoading = false,
  showFilters = true,
  showSort = true,
  sortOrder: initialSortOrder = 'desc',
  categories,
  onSortChange,
  onFilterChange,
  renderEvent,
  renderIndicator,
  renderFilter,
  renderShowAllToggle,
  renderSortToggle,
  getEventDate,
  dateFormat = 'year-month',
}: TimelineProps<T>) => {
  const timelineRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(initialSortOrder);

  // Get all unique categories from events if not provided
  const allCategories =
    categories || Array.from(new Set(events.map((event) => event.category)));

  const [filters, setFilters] = useState<FilterState>(() => {
    const initialFilters: FilterState = {};
    allCategories.forEach((category) => {
      initialFilters[category] = true;
    });
    return initialFilters;
  });

  // Helper functions for generic data
  const getDate = (event: T): string => {
    if (getEventDate) return getEventDate(event);
    if (event.date instanceof Date) {
      return event.date.toISOString();
    }
    return event.date;
  };

  const formatDateForDisplay = (
    dateString: string
  ): { year: string; month: string; day?: string; time?: string } => {
    const date = new Date(dateString);

    const year = date.getFullYear().toString();
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate().toString();
    const time = date.toLocaleTimeString('default', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    return { year, month, day, time };
  };

  const getCategory = (event: T): string => {
    return event.category;
  };

  const filteredAndSortedEvents = [...events]
    .filter((event) => filters[getCategory(event)])
    .sort((a, b) => {
      const dateA = new Date(getDate(a)).getTime();
      const dateB = new Date(getDate(b)).getTime();

      if (sortOrder === 'asc') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      {
        threshold: 0.05,
        rootMargin: '0px 0px -100px 0px',
      }
    );

    const timeoutId = setTimeout(() => {
      timelineRefs.current.forEach((ref, index) => {
        if (ref) {
          observer.observe(ref);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
      timelineRefs.current = [];
    };
  }, [filteredAndSortedEvents]);

  const toggleSortOrder = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    onSortChange?.(newOrder);
  };

  const toggleFilter = (category: string) => {
    const newFilters = {
      ...filters,
      [category]: !filters[category],
    };
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const toggleAllFilters = () => {
    const allActive = Object.values(filters).every(Boolean);
    const newFilters: FilterState = {};
    allCategories.forEach((category) => {
      newFilters[category] = !allActive;
    });
    setFilters(newFilters);
    onFilterChange?.(newFilters);
  };

  const getFilterColors = (category: string) => {
    const colors = [
      'timeline-filter-work',
      'timeline-filter-education',
      'timeline-filter-life',
      'timeline-filter-personal',
      'timeline-filter-professional',
      'timeline-filter-academic',
    ];
    const index = allCategories.indexOf(category);
    return colors[index % colors.length] || '';
  };

  const getFilterIcon = (category: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      work: (
        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v2z' />
        </svg>
      ),
      education: (
        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09v6.91L12 23 1 17.09V9l11-6z' />
        </svg>
      ),
      life: (
        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' />
        </svg>
      ),
      test: (
        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
        </svg>
      ),
      asd: (
        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M13 10V3L4 14h7v7l9-11h-7z' />
        </svg>
      ),
      personal: (
        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' />
        </svg>
      ),
      professional: (
        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M20 6h-4V4c0-1.11-.89-2-2-2h-4c-1.11 0-2 .89-2 2v2H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-6 0h-4V4h4v22z' />
        </svg>
      ),
      academic: (
        <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09v6.91L12 23 1 17.09V9l11-6z' />
        </svg>
      ),
    };

    return iconMap[category] || '📅';
  };

  if (isLoading) {
    return (
      <div className={`timeline-container ${className}`}>
        <div className='timeline-loading'>
          <div className='timeline-loading-item'>
            <div className='timeline-loading-line'></div>
            <div className='timeline-loading-line short'></div>
            <div className='timeline-loading-line medium'></div>
            <div className='timeline-loading-line long'></div>
            <div className='timeline-loading-line short'></div>
          </div>
          <div className='timeline-loading-item'>
            <div className='timeline-loading-line'></div>
            <div className='timeline-loading-line short'></div>
            <div className='timeline-loading-line medium'></div>
            <div className='timeline-loading-line long'></div>
            <div className='timeline-loading-line short'></div>
          </div>
          <div className='timeline-loading-item'>
            <div className='timeline-loading-line'></div>
            <div className='timeline-loading-line short'></div>
            <div className='timeline-loading-line medium'></div>
            <div className='timeline-loading-line long'></div>
            <div className='timeline-loading-line short'></div>
          </div>
          <div className='timeline-loading-item'>
            <div className='timeline-loading-line'></div>
            <div className='timeline-loading-line short'></div>
            <div className='timeline-loading-line medium'></div>
            <div className='timeline-loading-line long'></div>
            <div className='timeline-loading-line short'></div>
          </div>
          <div className='timeline-loading-item'>
            <div className='timeline-loading-line'></div>
            <div className='timeline-loading-line short'></div>
            <div className='timeline-loading-line medium'></div>
            <div className='timeline-loading-line long'></div>
            <div className='timeline-loading-line short'></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`timeline-container ${className}`}>
      {(showFilters || showSort) && (
        <div className='timeline-controls'>
          {/* Mobile Layout */}
          <div className='timeline-controls-mobile'>
            {showFilters && (
              <div className='timeline-filters'>
                {renderShowAllToggle ? (
                  renderShowAllToggle(
                    Object.values(filters).every(Boolean),
                    toggleAllFilters
                  )
                ) : (
                  <button
                    onClick={toggleAllFilters}
                    className={`timeline-filter-all ${
                      Object.values(filters).every(Boolean) ? 'active' : ''
                    }`}
                  >
                    <span className='text-sm'>
                      {Object.values(filters).every(Boolean) ? '👁️' : '👁️‍🗨️'}
                    </span>
                  </button>
                )}

                {allCategories.map((category) => {
                  const isActive = filters[category];
                  const handleClick = () => toggleFilter(category);

                  if (renderFilter) {
                    return (
                      <div key={category}>
                        {renderFilter(category, isActive, handleClick)}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={category}
                      onClick={handleClick}
                      className={`timeline-filter ${
                        isActive ? `active ${getFilterColors(category)}` : ''
                      }`}
                    >
                      {getFilterIcon(category)}
                      {isActive && (
                        <div className='timeline-filter-indicator'></div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {showSort &&
              (renderSortToggle ? (
                renderSortToggle(sortOrder, toggleSortOrder)
              ) : (
                <button onClick={toggleSortOrder} className='timeline-sort'>
                  <svg
                    className={`w-4 h-4 ${sortOrder === 'asc' ? 'rotate' : ''}`}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4'
                    />
                  </svg>
                </button>
              ))}
          </div>

          {/* Desktop Layout */}
          <div className='timeline-controls-desktop'>
            {showFilters && (
              <div className='timeline-filters'>
                {renderShowAllToggle ? (
                  renderShowAllToggle(
                    Object.values(filters).every(Boolean),
                    toggleAllFilters
                  )
                ) : (
                  <button
                    onClick={toggleAllFilters}
                    className={`timeline-filter-all ${
                      Object.values(filters).every(Boolean) ? 'active' : ''
                    }`}
                  >
                    <span className='text-sm'>
                      {Object.values(filters).every(Boolean) ? '👁️' : '👁️‍🗨️'}
                    </span>
                    <span className='text-sm font-medium'>
                      {Object.values(filters).every(Boolean)
                        ? 'Hide All'
                        : 'Show All'}
                    </span>
                  </button>
                )}

                {allCategories.map((category) => {
                  const isActive = filters[category];
                  const handleClick = () => toggleFilter(category);

                  if (renderFilter) {
                    return (
                      <div key={category}>
                        {renderFilter(category, isActive, handleClick)}
                      </div>
                    );
                  }

                  return (
                    <button
                      key={category}
                      onClick={handleClick}
                      className={`timeline-filter ${
                        isActive ? `active ${getFilterColors(category)}` : ''
                      }`}
                    >
                      {getFilterIcon(category)}
                      <span className='text-sm font-medium capitalize'>
                        {category}
                      </span>
                      {isActive && (
                        <div className='timeline-filter-indicator'></div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {showSort &&
              (renderSortToggle ? (
                renderSortToggle(sortOrder, toggleSortOrder)
              ) : (
                <button onClick={toggleSortOrder} className='timeline-sort'>
                  <span>
                    {sortOrder === 'asc' ? 'Newest First' : 'Oldest First'}
                  </span>
                  <svg
                    className={`w-4 h-4 ${sortOrder === 'asc' ? 'rotate' : ''}`}
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4'
                    />
                  </svg>
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className='timeline-container'>
        {/* Timeline Line */}
        <div className='timeline-line'>
          <div className='timeline-line-gradient'></div>
          <div className='timeline-line-blur-1'></div>
          <div className='timeline-line-blur-2'></div>
        </div>

        <div className='timeline-events'>
          {filteredAndSortedEvents.map((event, index) => {
            const eventId = event.id || `event-${index}`;
            const eventElement = renderEvent ? (
              <div
                key={eventId}
                ref={(el) => {
                  timelineRefs.current[index] = el;
                }}
                className='timeline-event'
              >
                {renderIndicator ? (
                  <div className='timeline-event-indicator'>
                    {renderIndicator(event, index)}
                  </div>
                ) : (
                  <div
                    className={`timeline-event-dot timeline-event-dot-${getCategory(
                      event
                    )}`}
                  >
                    <div className='timeline-event-dot-glow'></div>
                  </div>
                )}

                <div className='timeline-event-date'>
                  {(() => {
                    const formattedDate = formatDateForDisplay(getDate(event));
                    return (
                      <>
                        <div className='timeline-event-year'>
                          {formattedDate.year}
                        </div>
                        {(dateFormat === 'year-month' ||
                          dateFormat === 'year-month-day' ||
                          dateFormat === 'year-month-day-time') && (
                          <div className='timeline-event-month'>
                            {formattedDate.month}
                          </div>
                        )}
                        {(dateFormat === 'year-month-day' ||
                          dateFormat === 'year-month-day-time') && (
                          <div className='timeline-event-day'>
                            {formattedDate.day}
                          </div>
                        )}
                        {dateFormat === 'year-month-day-time' && (
                          <div className='timeline-event-time'>
                            {formattedDate.time}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {renderEvent(event, index)}
              </div>
            ) : (
              <TimelineEvent
                key={eventId}
                ref={(el) => {
                  timelineRefs.current[index] = el;
                }}
                event={event as any}
                index={index}
                renderIndicator={renderIndicator}
              />
            );
            return eventElement;
          })}
        </div>
      </div>
    </div>
  );
};

export default Timeline;
